import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dimensions } from '@opentok/client';
import { ShotsContext } from '../contexts/shots/provider';
import type { Shot, ShotFeed } from '../stores/types/shots';
import { GreenroomState } from '../stores/types/types';
import { LAYOUTS_BY_SIZE, VideoLayouts } from '../utils/video-layout';
import { OpenTokContext } from '../contexts/opentok/opentok-context';
import { FeedType } from '../components/general-components/dynamic-component';

const RESOLUTIONS_1080 = {
	HIGH: { height: 1080, width: 1920 },
	MEDIUM: { height: 540, width: 960 }, // 960x540
	LOW: { height: 270, width: 480 },
};

const RESOLUTIONS_720 = {
	HIGH: { height: 720, width: 1280 },
	MEDIUM: { height: 360, width: 640 },
	LOW: { height: 180, width: 320 },
};

enum RESOLUTIONS_ENUM {
	HIGH='HIGH',
	MEDIUM='MEDIUM',
	LOW='LOW',
}

const useResolutionUpdate = () => {
	const { shots } = useSelector((state: GreenroomState) => state.ShotsReducer);
	const { feedScaling } = useSelector((state: GreenroomState) => state.SettingsReducer);
	const { opentok } = useContext(OpenTokContext);
	const embed = useSelector((state: GreenroomState) => state.SessionReducer.embed);
	const {
		connectedPresenterVideos,
		onsiteLocationStreams,
	} = useSelector((state: GreenroomState) => state.VideoReducer);

	const [
		preferredResolutions,
		setPreferredResolutions,
	] =	useState<{ [key: string]: Dimensions }>({});

	const {
		currentShot: {
			shot_data: {
				order, shotFeeds, forcedLayout,
			},
		},
	} = useContext(ShotsContext);

	const determineResolution = (
		shotLayout: string,
		position: any,
		presenterProfile: ShotFeed,
		layoutSize: number |undefined,
	) => {
		// position can be either string or number
		const stringifiedPosition = position.toString();
		let thisShotShouldBe1080;
		switch (shotLayout) {
			case 'single cover':
			case 'single-padding cover':
			case 'picture-in-picture bottom left':
				thisShotShouldBe1080 = stringifiedPosition === '0';
				break;
			case 'double cover':
			case 'picture-in-picture bottom right':
			case 'picture-in-picture top left':
				thisShotShouldBe1080 = stringifiedPosition === '1';
				break;
			case 'vertical two cover':
				thisShotShouldBe1080 = true;
				break;
			default:
				thisShotShouldBe1080 = false;
		}
		if (thisShotShouldBe1080) {
			return RESOLUTIONS_ENUM.HIGH;// { height: 1080, width: 1920 };
		} if (presenterProfile.audioOnly) {
			return RESOLUTIONS_ENUM.LOW;// { height: 180, width: 320 };
		} if (layoutSize && layoutSize < 5) {
			return RESOLUTIONS_ENUM.MEDIUM;// { height: 720, width: 1280 };
		}
		return RESOLUTIONS_ENUM.LOW;// { height: 180, width: 320 }
	};

	const getStreamIdFromGrUser = (grUser: number) => connectedPresenterVideos.find(
		({ profile, isScreenShare }) => profile === grUser && !isScreenShare,
	)?.id ?? '';

	const preferredResolutionChanged = (key: string, newPreferredResolution: Dimensions) => {
		if (!preferredResolutions[key]) {
			return true;
		}
		return preferredResolutions[key].width !== newPreferredResolution.width
			|| preferredResolutions[key].height !== newPreferredResolution.height;
	};

	const getResolutionsForShotFeeds = (
		currentShotFeeds: ShotFeed[],
		currentFeed: ShotFeed,
		currentForcedLayout: string,
	) => {
		const layoutSize = LAYOUTS_BY_SIZE.find(
			(config: VideoLayouts) => config.layouts.includes(currentForcedLayout ?? ''),
		)?.presenters;
		const type = currentFeed.feedType;
		const inCurrentShotIndexPres = (
			currentShotFeeds.findIndex(
				(feed) => {
					if (type === FeedType.presenter) {
						return feed.presenter === currentFeed.presenter;
					}
					if (type === FeedType.onsiteLocation) {
						return feed.onsiteLocationId === currentFeed.onsiteLocationId;
					}
					return false;
				},
			)
		);

		const streamIds: string[] = [];

		if (type === FeedType.presenter && currentFeed.presenter) {
			streamIds.push(getStreamIdFromGrUser(currentFeed.presenter));
		}
		if (type === FeedType.onsiteLocation && currentFeed.onsiteLocationId) {
			const currLocationStreams = onsiteLocationStreams.get(currentFeed.onsiteLocationId);
			if (currLocationStreams) {
				streamIds.push(...currLocationStreams.map((stream) => stream.id));
			}
		}

		// presenter will always be one, but onsite locations may have several streams
		return streamIds.reduce((acc: { streamId: string, shotResolution: Dimensions }[], curr) => {
			const subscriber = opentok?.subscribers[curr];
			if (subscriber) {
				const RESOLUTIONS = subscriber.stream?.videoDimensions?.height === 720
					? RESOLUTIONS_720 : RESOLUTIONS_1080;
				const shotResolution = RESOLUTIONS[determineResolution(
					currentForcedLayout,
					currentShotFeeds[inCurrentShotIndexPres].position,
					currentFeed,
					layoutSize,
				)];

				acc.push({ streamId: curr, shotResolution });
			}
			return acc;
		}, []);
	};

	useEffect(() => {
		if (feedScaling && opentok && embed) {
			Object.entries(opentok?.subscribers).forEach(([, subscriber]) => {
				const RESOLUTIONS = subscriber.stream?.videoDimensions?.height === 720
					? RESOLUTIONS_720 : RESOLUTIONS_1080;
				subscriber.setPreferredResolution(RESOLUTIONS.HIGH);
			});
		}
	}, [connectedPresenterVideos, onsiteLocationStreams]);

	useEffect(() => {
		if (
			feedScaling
			&& opentok
			&& !embed
			&& (connectedPresenterVideos.length > 0 || onsiteLocationStreams.size > 0)
		) {
			// update resolution for presenters in the next shot/previous shot
			if (!order && order !== 0) {
				return;
			}

			const thisAndNextShotFeeds: {
				[key: string]: { currentShotResolution?: Dimensions, nextShotResolution?: Dimensions },
			} = {};

			// get number of feeds for current and next shot
			const feedsInCurrentShot = shotFeeds.map((feed) => feed);
			feedsInCurrentShot?.forEach((feedCurrent) => {
				const resolutions = getResolutionsForShotFeeds(shotFeeds, feedCurrent, forcedLayout);
				resolutions.forEach(({ streamId, shotResolution }) => {
					thisAndNextShotFeeds[streamId] = { currentShotResolution: shotResolution };
				});
			});

			const newNextShot: Shot | undefined = shots.find(
				(shot: Shot) => shot.shot_data.order === order + 1,
			);
			if (newNextShot) {
				const { forcedLayout: nForcedLayout, shotFeeds: nShotFeeds } = newNextShot.shot_data;
				const feedsInNextShot = nShotFeeds
					.reduce((accumulator: ShotFeed[], currentFeed: ShotFeed) => {
						if (currentFeed.presenter || currentFeed.onsiteLocationId) {
							accumulator.push(currentFeed);
						}
						return accumulator;
					}, []);
				feedsInNextShot?.forEach((feedNext) => {
					const resolutions = getResolutionsForShotFeeds(nShotFeeds, feedNext, nForcedLayout);
					resolutions.forEach(({ streamId, shotResolution }) => {
						thisAndNextShotFeeds[streamId] = {
							...thisAndNextShotFeeds[streamId],
							nextShotResolution: shotResolution,
						};
					});
				});
			}
			const doNotSetAsLowestResolution = Object.keys(thisAndNextShotFeeds);
			const presenterStreamIds = connectedPresenterVideos.map((v) => v.id);
			const locationStreamIds = [...onsiteLocationStreams.values()]
				.reduce((acc: string[], curr) => [...acc, ...curr.map((v) => v.id)], []);
			const allSubscriberKeys = Object.keys(opentok?.subscribers).filter((key) => (
				presenterStreamIds.includes(key) || locationStreamIds.includes(key)
			));
			const keysToSetAsLowestResolution = allSubscriberKeys.filter(
				(el) => !doNotSetAsLowestResolution.includes(el),
			);
			const changedPreferredResolutions: { [key: string]: Dimensions } = {};

			Object.entries(thisAndNextShotFeeds).forEach(([key, presenter]:[string, any]) => {
				const currentShotHeight = presenter.currentShotResolution?.height || 0;
				const nextShotHeight = presenter.nextShotResolution?.height || 0;
				const preferredResolution = currentShotHeight > nextShotHeight
					? presenter.currentShotResolution
					: presenter.nextShotResolution;

				const subscriber = opentok?.subscribers[key];
				if (preferredResolutionChanged(key, preferredResolution)) {
					changedPreferredResolutions[key] = preferredResolution;
					subscriber?.setPreferredResolution(preferredResolution);
				}
			});
			keysToSetAsLowestResolution.forEach((key) => {
				const subscriber = opentok?.subscribers[key];
				const preferredResolution = subscriber.stream?.videoDimensions?.height === 720
					? RESOLUTIONS_720.LOW : RESOLUTIONS_1080.LOW;
				if (preferredResolutionChanged(key, preferredResolution)) {
					changedPreferredResolutions[key] = preferredResolution;
					subscriber?.setPreferredResolution(preferredResolution);
				}
			});
			if (Object.entries(changedPreferredResolutions).length > 0) {
				setPreferredResolutions({
					...preferredResolutions,
					...changedPreferredResolutions,
				});
			}
		}
	}, [
		feedScaling,
		shots,
		connectedPresenterVideos,
		order,
		forcedLayout,
		shotFeeds,
		opentok,
		onsiteLocationStreams,
	]);
};

export default useResolutionUpdate;
